import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import ReservationTimer from '../components/ReservationTimer';
import ProviderImage from '../components/ProviderImage';
import { useAppState } from '../state/AppState';
import { setTimerStart, clearBlockHolds } from '../services/sessionService';
import { getConfig } from '../services/configService';
import { findMatchingAvailabilities, matchAvailability } from '../services/availabilitiesService';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { formatSessionDate, formatSessionTime } from '../utils/dateUtils';
import { hasInsurance, isSelfPay } from '../utils/insuranceUtils';

/**
 * ScreenN - Insurance confirmation screen.
 *
 * Allows the user to confirm their insurance selection and proceed based on their choice.
 */
const ScreenN: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();

  // Start the timer when Screen N loads
  useEffect(() => {
    if (!state.reservationStartTime) {
      const currentTime = Date.now();
      dispatch({ type: 'SET_RESERVATION_START_TIME', payload: currentTime });
      
      // Call API to set timer start time
      const callTimerAPI = async () => {
        try {
          const baseUrl = state.remoteApiUrl || 'http://localhost:5293';
          await setTimerStart(baseUrl, new Date(currentTime).toISOString());
        } catch (error) {
          // Continue even if API call fails
        }
      };
      
      callTimerAPI();
    }
  }, [state.reservationStartTime, dispatch, state.remoteApiUrl]);

  // Find and log matching availabilities based on user selections
  useEffect(() => {
    if (state.availabilities && state.availabilities.length > 0) {
      // Use service to find matching availabilities
      const { matching: matchingAvailabilities, nonMatching } = findMatchingAvailabilities(
        state.availabilities,
        {
          locationCode: state.locationCode,
          stateString: state.stateString,
          service: state.service,
          reason: state.reason,
          insurance: state.insurance,
          appointmentTime: state.appointmentTime,
          appointmentDate: state.appointmentDate
        }
      );
      
      // Log detailed matching criteria for debugging
      state.availabilities.forEach(availability => {
        const matchResult = matchAvailability(availability, {
          locationCode: state.locationCode,
          stateString: state.stateString,
          service: state.service,
          reason: state.reason,
          insurance: state.insurance,
          appointmentTime: state.appointmentTime,
          appointmentDate: state.appointmentDate
        });
        
        if (matchResult.isMatch) {
        } else {
        }
      });
      // Set the first matching availability as selected (handle 0, 1, or >1 cases)
      if (matchingAvailabilities.length === 0) {
        dispatch({ type: 'SET_SELECTED_AVAILABILITY', payload: null });
      } else if (matchingAvailabilities.length === 1) {
        dispatch({ type: 'SET_SELECTED_AVAILABILITY', payload: matchingAvailabilities[0] });
      } else {
        
        dispatch({ type: 'SET_SELECTED_AVAILABILITY', payload: matchingAvailabilities[0] });
      }
      
      if (matchingAvailabilities.length > 0) {
        
        
        // Log the complete availability objects for detailed analysis
        
        matchingAvailabilities.forEach((av, index) => {
        });
      } else {
        // Show some sample availabilities for debugging
        
      }
    }
  }, [state.availabilities, state.providerId, state.visitType, state.location, state.locationCode, 
      state.service, state.reason, state.insurance, state.appointmentDate, state.appointmentTime, 
      state.appointmentDay, state.stateString]);

  // Get provider name from availability data
  const providerName = getProviderNameFromAvailability(state.availabilities, null);
  
  // Get session rate from selected availability based on service type
  const getSessionRate = (): string => {
    const fallbackRate = 'Please call (425) 640-7009 for rate.';
    
    if (!state.selectedAvailability) {
      return fallbackRate;
    }
    
    const availability = state.selectedAvailability as any; // Type assertion for pricing properties
    const service = state.service;
    
    // Check service type and use appropriate pricing
    if (service === 'Individual Therapy' && availability.privatePayIndividualTherapy) {
      return availability.privatePayIndividualTherapy;
    } else if ((service === 'Couples Therapy' || service === 'Family Therapy') && availability.privatePayCouplesFamily) {
      return availability.privatePayCouplesFamily;
    }
    
    return fallbackRate;
  };
  
  // Format date without year: "Monday, Jan 7"
  const formatDateShort = (dateString: string | null): string => {
    try {
      if (!dateString) return '';
      
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return '';
    }
  };
  
  // Get session duration from availability or default to 50 minutes
  const getSessionDuration = (): string => {
    if (state.selectedAvailability) {
      const availability = state.selectedAvailability as any;
      // Check if interval exists and convert to minutes
      if (availability.interval) {
        // Interval might be in minutes or as a string like "50"
        const intervalNum = parseInt(availability.interval);
        if (!isNaN(intervalNum)) {
          return `${intervalNum}min Session`;
        }
      }
    }
    return '';
  };
  
  const sessionDate = formatDateShort(state.appointmentDate);
  const sessionTime = formatSessionTime(state.appointmentTime);
  const sessionLocation = state.location || 'Northgate Office';
  const sessionRate = getSessionRate();
  const sessionDuration = getSessionDuration();

  const handleBack = () => {
    // Clear block holds when user clicks back (don't wait for result)
    const config = getConfig();
    const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';
    clearBlockHolds(baseUrl).catch(error => {
    });
    navigate(-1);
  };

  const handleContinue = () => {
    navigate('/p');
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={handleBack} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card mb-18 text-center" style={{ borderRadius: 16, overflow: 'hidden', paddingTop: 8 }}>
            {state.reservationStartTime && (
              <div className="text-soft" style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontSize: '1.05rem', fontWeight: 500 }}>
                <ReservationTimer 
                  style={{ color: '#555555', fontWeight: 400 }}
                />
              </div>
            )}
            <h2 className="mtg-screen-question mb-18">
              Almost there!
            </h2>
            <div id="reservation-description" className="mb-18 text-soft" style={{ fontSize: '1.1rem', fontWeight: 400 }}>
              Your appointment will be held for 15 minutes while you fill out the intake form below.
            </div>
            <div style={{ padding: 0, background: '#fff', borderRadius: 16, minWidth: 260 }}>
              <ProviderImage className="mb-12 mt-12 d-block" maxWidth={320} maxHeight={320} style={{ margin: '0 auto' }} />
              <h3 className="mb-2 darkest-blue text-provider-name">{providerName}</h3>
              <div className="mb-2 text-soft">{sessionDate}</div>
              <div className="mb-2 text-soft">{sessionTime}</div>
              <div className="mb-2 text-soft">{sessionLocation}</div>
              <hr className="horizontal-line mt-18" style={{ borderTop: '2px solid #999999' }}/>
              {isSelfPay(state.insurance) ? (
                <>
                <div className="mb-2 text-soft">Private Pay Rate: {sessionRate}</div>
                <div className="mb-18 text-soft">{sessionDuration}</div>
                </>
              ) : (
                <>
                  <div className="mb-18 text-soft">Insurance:<br/>{state.insurance}</div>
                  <div className="mb-24 text-soft">
                    Please have your insurance card(s) ready, including your primary insurance and any secondary or tertiary policies. You can use either a physical card or a digital copy.
                  </div>
                </>
              )}
            </div>
            <button
              className="pill-btn-standard"
              onClick={handleContinue}
              aria-labelledby="reservation-title"
              aria-describedby="reservation-description"
            >
              Continue
            </button>
            <ProgressBar progress={90}/>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ScreenN; 